import React from "react";
import { Button, Input } from "semantic-ui-react";
import { WidgetProps } from "../types";
import Prompt from "../Prompt";
import Summary, { SummaryField, SummaryPills, Actions } from "../Summary";

/** "Heroes" — up to three heroes, three admired qualities each. */
const QUALITIES_PER_HERO = 3;
const MAX_HEROES = 3;

interface Hero {
  name: string;
  qualities: string[];
}

const blankHero = (): Hero => ({
  name: "",
  qualities: Array(QUALITIES_PER_HERO).fill(""),
});

const HeroesDiscovery: React.FC<WidgetProps> = ({
  payload,
  update,
  submit,
  submitting,
  finished,
  question,
  currentUser,
}) => {
  const heroes: Hero[] = payload.heroes?.length ? payload.heroes : [blankHero()];

  const setHeroes = (next: Hero[]) => update({ ...payload, heroes: next });

  const editHero = (index: number, changes: Partial<Hero>) =>
    setHeroes(heroes.map((h, i) => (i === index ? { ...h, ...changes } : h)));

  const editQuality = (heroIndex: number, qualityIndex: number, value: string) =>
    editHero(heroIndex, {
      qualities: heroes[heroIndex].qualities.map((q, i) =>
        i === qualityIndex ? value : q,
      ),
    });

  const named = heroes.filter((h) => (h.name || "").trim());
  const complete =
    named.length > 0 &&
    named.every((h) => h.qualities.some((q) => (q || "").trim()));

  if (finished) {
    return (
      <Summary question={question} currentUser={currentUser}>
        {heroes
          .filter((h) => (h.name || "").trim())
          .map((hero, i) => (
            <SummaryField key={i} label={hero.name}>
              <SummaryPills values={hero.qualities || []} />
            </SummaryField>
          ))}
      </Summary>
    );
  }

  return (
    <div className="ui form assignment-form">
      <Prompt question={question} currentUser={currentUser} />

      {heroes.map((hero, i) => (
        <div key={i} className="ix-section ix-hero">
          <div className="ix-label">Hero {i + 1}</div>

          <div className="ix-hero-head">
            <Input
              fluid
              size="large"
              className="ix-hero-name"
              placeholder="Who is your hero?"
              value={hero.name || ""}
              onChange={(e) => editHero(i, { name: e.target.value })}
            />
            {heroes.length > 1 && (
              <Button
                icon="x"
                basic
                circular
                type="button"
                aria-label={`Remove ${hero.name || "hero"}`}
                onClick={() => setHeroes(heroes.filter((_, j) => j !== i))}
              />
            )}
          </div>

          <div className="ix-hero-qualities">
            {hero.qualities.map((quality, j) => (
              <Input
                key={j}
                fluid
                placeholder={
                  hero.name
                    ? `What do you admire about ${hero.name}?`
                    : `What do you admire about them? (${j + 1} of ${QUALITIES_PER_HERO})`
                }
                value={quality || ""}
                onChange={(e) => editQuality(i, j, e.target.value)}
              />
            ))}
          </div>
        </div>
      ))}

      <Actions>
        {heroes.length < MAX_HEROES && (
          <Button
            basic
            circular
            primary
            type="button"
            onClick={() => setHeroes([...heroes, blankHero()])}
          >
            Add another hero
          </Button>
        )}

        <Button
          primary
          circular
          className="challenge-submit"
          disabled={!complete || submitting}
          loading={submitting}
          onClick={submit}
        >
          Submit
        </Button>
      </Actions>
    </div>
  );
};

export default HeroesDiscovery;
